import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { createRichtext } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import RichTextView from "@/components/previews/RichTextView";
import PortalNotesFields from "@/components/PortalNotesFields";

export default function RichTextBuilder() {
  const nav = useNavigate();
  const [title, setTitle] = useState("Welcome");
  const [description, setDescription] = useState("A short blurb for preview cards.");
  const [content, setContent] = useState(
    "<p>Write <strong>beautifully</strong> formatted text. Drop in an <em>image</em>, add a <a href=\"https://portalstudio.app\">link</a>, or paste in content from anywhere.</p>"
  );
  const [portals, setPortals] = useState([]);
  const [notes, setNotes] = useState("");
  const [tags, setTags] = useState([]);
  const [folderId, setFolderId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const previewData = useMemo(
    () => ({ title, description, content_html: content }),
    [title, description, content]
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Title is required"); return; }
    // strip-tags length check
    const plain = content.replace(/<[^>]+>/g, "").trim();
    if (!plain) { toast.error("Add some content first"); return; }

    setSubmitting(true);
    try {
      const res = await createRichtext({
        title, description, content_html: content, portals, notes, tags, folder_id: folderId,
      });
      toast.success("Rich text created");
      nav(`/success/richtext/${res.id}`);
    } catch (err) {
      if (err?.response?.status === 413) {
        toast.error("Content too large. Try fewer / smaller images.");
      } else {
        toast.error("Failed to create. Try again.");
      }
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-10">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-3">New Component</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold text-zinc-950">
            Build a Rich Text page
          </h1>
          <p className="text-zinc-600 mt-2">
            Formatted copy, headings, lists, links, and inline images — embeddable anywhere.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <form data-testid="builder-form" onSubmit={onSubmit} className="md:col-span-7 space-y-8">
            <section className="border border-zinc-200 bg-white p-6 rounded-md space-y-5">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">Heading</div>
              <div>
                <Label className="text-xs text-zinc-500">Title</Label>
                <Input
                  data-testid="richtext-title-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Description <span className="text-zinc-400">(used as preview / og description)</span></Label>
                <Textarea
                  data-testid="richtext-description-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="mt-1.5 resize-none"
                  rows={2}
                />
              </div>
            </section>

            <section className="space-y-3">
              <div className="flex items-baseline justify-between">
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">Content</div>
                <span className="text-[11px] text-zinc-400">
                  Images are uploaded to PortalStudio · up to 10 MB each
                </span>
              </div>
              <RichTextEditor value={content} onChange={setContent} />
            </section>

            <PortalNotesFields portals={portals} setPortals={setPortals}
                              notes={notes} setNotes={setNotes}
                              tags={tags} setTags={setTags}
                              folderId={folderId} setFolderId={setFolderId} />

            <Button
              type="submit"
              data-testid="generate-embed-btn"
              disabled={submitting}
              className="h-12 px-6 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 transition-all duration-200"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Generate embed URL
            </Button>
          </form>

          <div className="md:col-span-5">
            <div className="md:sticky md:top-24">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-3">Live Preview</div>
              <div data-testid="preview-container" className="border border-zinc-200 rounded-md bg-white overflow-hidden">
                <div className="border-b border-zinc-100 px-4 py-2 flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                  <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                  <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                  <span className="ml-3 text-[11px] font-mono text-zinc-400">embed/richtext/preview</span>
                </div>
                <div className="p-6 bg-white min-h-[480px]">
                  <RichTextView data={previewData} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
